import {
  AssignmentBlockOutputCreateModel,
  AssignmentBlockOutputResultModel,
  AssignmentBlockUserInputModel,
  AssignmentModel,
  AssignmentsResponseModel,
  BlockOutputResultAsMentorItem,
  DeprecatedAssignmentModel,
  EvaluateBlockInputResponse,
  UploadedFileModel,
  UserInputType
} from '@models/assignments';
import { CreateResponseModel, SuccessResponseModel } from '@models/common';
import * as Sentry from '@sentry/nextjs';
import { useCallback } from 'react';
import { useImprvApi } from './useImprvApi';

export function mergeAssignmentWithMentorResults(
  assignment: AssignmentModel,
  mentorResults: BlockOutputResultAsMentorItem[] | null,
  goalSubscriptionId: number
): AssignmentModel {
  if (!mentorResults || !assignment.blockOutputResults) return assignment;

  const mentorItem = mentorResults.find(
    (item: BlockOutputResultAsMentorItem) => item.goalSubscriptionId === goalSubscriptionId
  );
  if (!mentorItem) return assignment;

  const mentorMap = new Map(mentorItem.blockOutputResults.map((bor) => [bor.blockEntityId, bor]));

  return {
    ...assignment,
    blockOutputResults: assignment.blockOutputResults.map(
      (bor): AssignmentBlockOutputResultModel => {
        const mentorBor = mentorMap.get(bor.blockEntityId);
        if (!mentorBor) return bor;
        const uploadedFilesFromMentor =
          mentorBor.userInput?.type === UserInputType.FILE_UPLOAD_CONFIRMATION
            ? (mentorBor.userInput.value as UploadedFileModel[])
            : undefined;
        return {
          ...bor,
          uploadedFiles: uploadedFilesFromMentor ?? bor.uploadedFiles,
          uploadedMediaitems: mentorBor.uploadedMediaitems ?? bor.uploadedMediaitems,
          feedback: mentorBor.feedback ?? null
        };
      }
    )
  };
}

/**
 * Custom hook for managing assignment-related operations
 */
export const useAssignmentsService = () => {
  const imprvApi = useImprvApi();

  /**
   * Checks the user input for a specific block in a node-based assignment
   * @param nodeEntityId The ID of the node entity
   * @param goalSubscriptionId The ID of the goal subscription
   * @param blockUserInput The user input data for the block
   * @returns A promise resolving to a success response
   */
  const evaluateBlockUserInput = useCallback(
    async (
      nodeEntityId: string,
      goalSubscriptionId: number,
      blockUserInput: AssignmentBlockUserInputModel
    ): Promise<EvaluateBlockInputResponse> => {
      try {
        return await imprvApi.evaluateBlockUserInputForNode(
          nodeEntityId,
          goalSubscriptionId,
          blockUserInput
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  /**
   *
   * @deprecated
   * @returns
   */
  const getAssignmentById = useCallback(
    async (assignmentId: string): Promise<DeprecatedAssignmentModel> => {
      try {
        return await imprvApi.getAssignmentById(assignmentId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  /**
   * Creates a new task assignment
   * @param nodeEntityId The ID of the node entity
   * @param goalSubscriptionId The ID of the goal subscription
   * @returns A promise resolving to the created assignment response
   */
  const createTaskAssignment = useCallback(
    async (nodeEntityId: string, goalSubscriptionId: number): Promise<CreateResponseModel> => {
      try {
        return await imprvApi.createTaskAssignment({
          node_entity_id: nodeEntityId,
          goal_subscription_id: goalSubscriptionId
        });
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getAssignmentsByNode = useCallback(
    async (
      nodeEntityId: string,
      goalSubscriptionId?: number
    ): Promise<AssignmentsResponseModel> => {
      try {
        return await imprvApi.getAssignmentsByNode(nodeEntityId, goalSubscriptionId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getAssignmentByNode = useCallback(
    async (nodeEntityId: string, goalSubscriptionId?: number): Promise<AssignmentModel> => {
      try {
        return await imprvApi.getAssignmentByNode(nodeEntityId, goalSubscriptionId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getAssignmentWithBlockOutputs = useCallback(
    async (nodeEntityId: string, goalSubscriptionId: number): Promise<AssignmentsResponseModel> => {
      try {
        return await imprvApi.getAssignmentWithBlockOutputs(nodeEntityId, goalSubscriptionId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const createBlockOutputLogsForNode = useCallback(
    async (
      nodeEntityId: string,
      goalSubscriptionId: number,
      blockOutputLogs: AssignmentBlockOutputCreateModel[]
    ): Promise<SuccessResponseModel> => {
      try {
        return await imprvApi.createBlockOutputsForNode(
          nodeEntityId,
          goalSubscriptionId,
          blockOutputLogs
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getBlockOutputResultsAsMentor = useCallback(
    async (nodeEntityId: string, userId?: number): Promise<BlockOutputResultAsMentorItem[]> => {
      try {
        return await imprvApi.getBlockOutputResultsAsMentor(nodeEntityId, userId);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const getBlockOutputResults = useCallback(
    async (
      {
        nodeEntityId,
        goalSubscriptionId
      }: {
        nodeEntityId: string;
        goalSubscriptionId: number;
      },
      options?: { signal?: AbortSignal }
    ): Promise<AssignmentBlockOutputResultModel[]> => {
      try {
        const assignment = await imprvApi.getAssignmentByNode(
          nodeEntityId,
          goalSubscriptionId,
          options
        );
        return assignment.blockOutputResults ?? [];
      } catch (error) {
        if (error?.name !== 'AbortError') {
          Sentry.captureException(error);
        }
        throw error;
      }
    },
    [imprvApi]
  );

  const createBlockOutputsAsMentor = useCallback(
    async (
      nodeEntityId: string,
      userId: number,
      goalSubscriptionId: number,
      blockOutputs: AssignmentBlockOutputCreateModel[]
    ): Promise<SuccessResponseModel> => {
      try {
        return await imprvApi.createBlockOutputsAsMentor(
          nodeEntityId,
          userId,
          goalSubscriptionId,
          blockOutputs
        );
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  const updateGoalSubscription = useCallback(
    async (
      goalSubscriptionId: number,
      payload: { status?: string; priority?: string; deadline?: string }
    ): Promise<SuccessResponseModel> => {
      try {
        return await imprvApi.updateGoalSubscription(goalSubscriptionId, payload);
      } catch (error) {
        Sentry.captureException(error);
        throw error;
      }
    },
    [imprvApi]
  );

  return {
    evaluateBlockUserInput,
    createTaskAssignment,
    getAssignmentsByNode,
    getAssignmentByNode,
    getAssignmentWithBlockOutputs,
    createBlockOutputLogsForNode,
    updateGoalSubscription,
    getAssignmentById,
    getBlockOutputResultsAsMentor,
    getBlockOutputResults,
    createBlockOutputsAsMentor
  };
};
